/**
 * "How to make a dashboard": a new dashboard, the chart from How to plot added
 * from the Catalog, the dashboard's time, moving and resizing a tile,
 * unlinking it from the Catalog and editing it, then a new chart and Save.
 *
 * It needs How to plot finished first: that tour saves the chart this one adds.
 * Without a database there is no Catalog, so the tour starts with a new chart
 * and leaves out the Catalog and unlink steps.
 */

import { catalogAvailable } from '../../../state/store.ts';
import { isPageVisible } from '../../../state/page-visibility.ts';
import { dashboardSelection } from '../../dashboard/dashboard-selection.ts';
import {
  activeDashboard,
  activeTab,
  closeDashboard,
  dashboardDirty,
  dashboardEditing,
  dashboardRoute,
  type DashboardTile,
} from '../../dashboard/dashboard-state.ts';
import { registerTour, type TourStep } from '../tour-registry.ts';

/** The tile this tour works on: the first one it added. */
let tileId: string | null = null;
/** What a `when` step measures its change from, set as the step starts. */
let before = '';
let tileCount = 0;

const chartTiles = (): DashboardTile[] => (activeTab()?.tiles ?? []).filter((t) => t.kind === 'chart');
const tourTile = (): DashboardTile | null => chartTiles().find((t) => t.id === tileId) ?? null;
const tileCell = (): HTMLElement | null =>
  tileId ? document.querySelector<HTMLElement>(`.dash-grid-cell[data-tile-id="${CSS.escape(tileId)}"]`) : null;
const builderOpen = (): boolean => document.querySelector('chart-builder') !== null;
const pickerOpen = (): boolean => document.querySelector('catalog-picker') !== null;

/** Start counting chart tiles, so the step sees the one that was added. */
const countTiles = (): void => {
  tileCount = chartTiles().length;
};
/** True when a chart tile was added. The first one added becomes the tour's tile. */
const tileAdded = (linked: boolean): boolean => {
  const tiles = chartTiles();
  if (tiles.length <= tileCount) return false;
  const added = tiles[tiles.length - 1]!;
  if (linked && !added.chartId) return false;
  tileId ??= added.id;
  return true;
};
const pos = (): string => {
  const p = tourTile()?.pos;
  return p ? `${p.x},${p.y},${p.w},${p.h}` : '';
};
const size = (): string => pos().split(',').slice(2).join(',');
const place = (): string => pos().split(',').slice(0, 2).join(',');

const newChartSteps = (needs: () => boolean, text: string): TourStep[] => [
  {
    target: 'dash-add-chart',
    title: 'Add a new chart',
    text,
    advance: { when: builderOpen },
    needs,
    blocked: ['dash-add-catalog'],
  },
  {
    target: 'chart-builder',
    title: 'Build the chart',
    text:
      'These are the Explore controls. Pick a signal on the Y axis, as in How to plot. '
      + 'Then click the blue Add to dashboard button in the top-right corner.',
    enter: countTiles,
    advance: { when: () => tileAdded(false) },
    needs,
    backIfGone: true,
  },
];

registerTour({
  id: 'first-dashboard',
  version: 1,
  title: 'How to make a dashboard',
  description: 'Put your chart on a dashboard, set the time, move and resize it, edit it, and add a new chart.',
  minutes: 5,
  requires: ['first-plot'],
  available: () => isPageVisible('dashboards'),
  prepare: () => {
    tileId = null;
    // Start from the list of dashboards. An open dashboard with unsaved work
    // stays open: closing it here would lose that work.
    if (!dashboardDirty.value) closeDashboard();
    dashboardEditing.set(false);
  },
  steps: [
    {
      target: 'nav-dashboards',
      title: 'Open Dashboards',
      text: 'A dashboard puts many charts on one page. Click Dashboards.',
      advance: 'click',
    },
    {
      target: 'dash-new',
      title: 'Make a dashboard',
      text: 'Click New dashboard. It opens empty.',
      advance: { when: () => dashboardRoute.value === 'view' && chartTiles().length === 0 },
    },
    {
      target: 'dash-name',
      title: 'Give it a name',
      text: 'Click the name to change it. Type a name and press Enter. You can also do this later.',
      advance: 'next',
    },

    // Without a Catalog: the first chart is a new one.
    ...newChartSteps(() => !catalogAvailable.value, 'Click Add chart.'),

    // With a Catalog: the easy way first, the chart saved in How to plot.
    {
      target: 'dash-add-chart',
      title: 'Add your saved chart',
      text:
        'For now, use the chart you saved in How to plot. Click Add chart, then From catalog. '
        + 'New chart is in the same menu. You use it later in this tour.',
      advance: { when: pickerOpen },
      blocked: ['dash-add-new'],
      needs: () => catalogAvailable.value,
    },
    {
      target: 'catalog-picker',
      title: 'Pick the chart',
      text: 'Click the chart you saved in How to plot, then click Add 1 chart at the bottom.',
      enter: countTiles,
      advance: { when: () => tileAdded(true) },
      needs: () => catalogAvailable.value,
      backIfGone: true,
    },
    {
      target: () => tileCell(),
      title: 'Linked to the Catalog',
      text:
        'The chain icon in the title means this chart is linked to the Catalog. '
        + 'When the chart changes in the Catalog, it changes here too.',
      advance: 'next',
      needs: () => catalogAvailable.value,
    },

    {
      target: 'dash-ships',
      title: 'Ships for all charts',
      text:
        'Each chart shows the ships picked here, unless it was saved with its own ship. '
        + 'If no ship is picked, click here and pick one.',
      advance: {
        when: () => {
          const d = activeDashboard();
          return !!d && dashboardSelection(d).ships().length > 0;
        },
      },
    },
    {
      target: 'dash-time',
      title: 'One time for all charts',
      text:
        'The dashboard time applies to every chart. Click it and choose another period, '
        + 'for example Last 30 days. Then click Next.',
      advance: 'next',
    },
    {
      target: 'dash-edit',
      title: 'Change the layout',
      text: 'Click Edit. Then you can move, resize and change the charts.',
      advance: { when: () => dashboardEditing.value },
    },
    {
      target: () => tileCell(),
      title: 'Resize the chart',
      text: 'Drag the bottom-right corner of the chart to make it bigger.',
      enter: () => {
        before = size();
      },
      advance: { when: () => size() !== '' && size() !== before },
      skippable: true,
    },
    {
      target: 'dash-grid',
      title: 'Move the chart',
      text: 'Drag the chart by its title to a new place, for example to the right.',
      enter: () => {
        before = place();
      },
      advance: { when: () => place() !== '' && place() !== before },
      skippable: true,
    },
    {
      target: () => tileCell()?.querySelector<HTMLElement>('[data-tour="tile-detach"]') ?? null,
      title: 'Unlink to edit here',
      text:
        'A linked chart shows the Catalog chart as it is. To change it only on this dashboard, '
        + 'unlink it. Click the unlink button.',
      advance: { when: () => tourTile()?.chartId === null },
      needs: () => catalogAvailable.value,
    },
    {
      target: () => tileCell()?.querySelector<HTMLElement>('[data-tour="tile-edit"]') ?? null,
      title: 'Edit the chart',
      text: 'The chart on this dashboard is its own copy now. Click the pencil to edit it.',
      advance: { when: builderOpen },
    },
    {
      target: 'chart-builder',
      title: 'Change something small',
      text:
        'For example, change the title at the top left, or add a second signal on the Y axis. '
        + 'Then click the blue Save changes button in the top-right corner.',
      enter: () => {
        const t = tourTile();
        before = t ? `${t.title}\n${t.query}` : '';
      },
      advance: {
        when: () => {
          const t = tourTile();
          return !!t && !builderOpen() && `${t.title}\n${t.query}` !== before;
        },
      },
      backIfGone: true,
    },

    // With a Catalog: the second way, a chart made for this dashboard only.
    ...newChartSteps(
      () => catalogAvailable.value,
      'The other way is a chart made only for this dashboard. Click Add chart, then New chart.',
    ),

    {
      target: 'dash-save',
      title: 'Save the dashboard',
      text: 'Changes on a dashboard are not kept until you save. Click Save.',
      advance: { when: () => !dashboardDirty.value },
    },
    {
      target: 'dash-grid',
      title: 'Your dashboard is ready',
      text:
        'Click Done to stop editing. Find the dashboard again under Dashboards. '
        + 'To share it, right-click it in the list.',
      advance: 'next',
    },
  ],
});
